import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'app/shared/button';
import Form from 'app/shared/form';
import { SimpleInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';
import { capitalizeText } from 'app/shared/utils/general';
import { updateEffectInStore } from 'store/actions/effect';
import { createValidationConfig } from 'app/shared/utils/form/validation';
import useValidator from 'hooks/validation';
import { useAutomationEffectService } from 'hooks/api/automata/effect';

const CreateEffectForm = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigateTo = useNavigate();
  const { fetchEffect, updateEffect, seedEffects } = useAutomationEffectService();

  const [loading, setLoading] = useState(false);
  const [is_seeding, setIsSeeding] = useState(false);
  const [data, setData] = useState({
    name: '',
    code: '',
    resource_class: ''
  });
  const { errors, validateField, form_is_valid } = useValidator(createValidationConfig);

  useEffect(() => {
    if (id) {
      fetchEffect(id).then(async ({ effect }) => {
        if (!effect) return;

        const { code, name, resource_class, in_use } = effect;
        const updated_code = code.split(':')[0];
        setData((previousValues) => ({
          ...previousValues,
          code: updated_code,
          name,
          resource_class,
          in_use
        }));
      });
    }
  }, []);

  const resource_class_list = [
    {
      label: 'Email',
      value: 'email'
    },
    {
      label: 'Link',
      value: 'link'
    },
    {
      label: 'Audience',
      value: 'audience'
    },
    {
      label: 'Contact',
      value: 'contact'
    }
  ];

  const handleSeedAction = async () => {
    setIsSeeding(() => true);
    await seedEffects();
    setIsSeeding(false);
    navigateTo(`/settings/effect`);
  };

  const submitForm = async () => {
    if (!id) return;

    setLoading(true);
    const result = await updateEffect(id, { data });

    if (result) {
      const values = { ...data, code: data.code, id };
      dispatch(updateEffectInStore(values));
    }
    navigateTo(`/settings/effect`);
  };

  const handleChange = (effect) => {
    const { name, value } = effect;
    const code_in_use = id && data.in_use;

    if (name === 'name') {
      const lowercaseValue = value.toLowerCase().replace(/ /g, '_');
      setData((prevData) => ({
        ...prevData,
        name: value,
        code: code_in_use ? prevData.code : lowercaseValue
      }));

      validateField(name, value);
      validateField('code', lowercaseValue);
    } else {
      let processedValue = value;
      if (name === 'code' && code_in_use) return;
      if (name === 'code') {
        processedValue = value.toLowerCase().replace(/ /g, '_');
      }
      setData((prevData) => ({
        ...prevData,
        [name]: processedValue
      }));

      validateField(name, processedValue);
    }
  };

  return (
    <Form className="effect-form">
      <h5 className="mb-4">Edit Effect</h5>
      <hr />
      <div className="grid">
        <SimpleInput
          label="NAME *"
          input_id="name"
          name="name"
          value={data.name}
          onInput={handleChange}
          error={errors.name}
        />
        <SimpleInput
          label="CODE *"
          input_id="code"
          name="code"
          value={data.code}
          onInput={handleChange}
          error={errors.code}
        />
        <div className="form-group">
          <label>RESOURCE CLASS *</label>
          <MultiSelect
            options={resource_class_list}
            onChange={(v) => handleChange({ name: 'resource_class', value: v.value })}
            value={{ value: data.resource_class, label: capitalizeText(data.resource_class) }}
            isMulti={false}
            error={errors.resource_class}
          />
        </div>
      </div>

      <div className="grid mt-2">
        <Button
          onClick={submitForm}
          text="Save"
          className="float-right"
          loading={loading}
          disabled={!id || !form_is_valid}
        />
      </div>
      <hr />
      <div className="grid mt-4">
        <Button
          onClick={handleSeedAction}
          text="Create Effects"
          className="float-right"
          loading={is_seeding}
        />
      </div>
    </Form>
  );
};

export default CreateEffectForm;
